import React, { useState } from 'react';
import { Menu, MenuItem, MenuSubItem } from '../components/ui';
import { MainItems, MenuItems } from './types';
import { useTheme } from '@material-ui/core/styles';
import styled from 'styled-components';
import history from '../store/history';
import { getRouteByName } from '../router/routes';
import { Link } from '../components/ui';
import { Conference, Track } from '../store/conference/types';
import { AddButton } from '../components/ui/inputs/AddButton/AddButton';
import { useTranslation, Trans } from 'react-i18next';
import { getMenuItemName } from '../helpers/translations';
import BookOpen from '../icons/BookOpen';
import Close from '../icons/Close';
import Underconstruction from '../icons/Underconstruction';
import { Button } from '../components/ui';

type Props = {
  main_items: MainItems;
  selectedOption: string | null;
  doClick: (item: MenuItems, isMenuSubitem: boolean) => void;
  logo: React.ReactNode;
  header: React.ReactNode;
  menuOpen: boolean;
  userName: string;
  children: React.ReactNode;
  isImpersonated?: boolean;
  currentConference?: Conference | null;
  currentTrack?: Track | null;
  leaveImpersonationAction?: () => void;
  backgroundColor: string;
  allowMobile: boolean;
  menuMobileOpen: boolean;
  handleOnMenuToggle: () => void;
  isMobile: boolean;
  isChair: boolean;
};

const MainLayout: React.FC<Props> = ({
  main_items,
  selectedOption,
  doClick,
  logo,
  header,
  menuOpen,
  userName,
  children,
  isImpersonated,
  leaveImpersonationAction,
  backgroundColor,
  currentConference,
  currentTrack,
  allowMobile,
  menuMobileOpen,
  handleOnMenuToggle,
  isMobile,
  isChair,
}) => {
  const { t } = useTranslation();

  const parentName = main_items.find(
    (parent) => parent.subitems && parent.subitems.some((subitem) => subitem.name === selectedOption),
  )?.name;

  const [expandedItems, setExpandedItems] = useState<string[]>([parentName !== undefined ? parentName : '']);

  const theme = useTheme();

  const menuItems = main_items.map((item, index) => {
    const selected =
      selectedOption === item.name || item.subitems?.find((value) => value.name === selectedOption) !== undefined;
    const iconProps = selected ? { stroke: theme.palette.primary.main } : {};

    return (
      <StyledMenuItem
        key={index}
        // @ts-ignore
        icon={React.createElement(item.icon, iconProps)}
        label={getMenuItemName(item.name, t)}
        selected={selected}
        color="primary"
        onClick={() => {
          setExpandedItems((itemsList) => {
            if (itemsList.includes(item.name)) {
              return itemsList.filter((i) => i !== item.name);
            } else {
              return [item.name];
            }
          });
          doClick(item, false);
        }}
        expanded={expandedItems.includes(item.name)}
        disabled={(isMobile && isChair) ?? false}
      >
        {item.subitems?.map((subitem) => {
          return (
            <MenuSubItem
              key={subitem.name}
              icon={subitem.icon}
              warning={subitem.warning}
              label={subitem.displayName ?? getMenuItemName(subitem.name, t)}
              selected={selectedOption === subitem.name}
              color="primary"
              onClick={() => {
                setExpandedItems((prevExpandedItems) => [item.name]);
                doClick(subitem, true);
              }}
              disabled={(isMobile && isChair) ?? false}
            />
          );
        })}
      </StyledMenuItem>
    );
  });

  const mobileUnderconstruction = (
    <div className="h-full flex justify-cente r items-center mobileCtrlUnderconstruction">
      <StyledMessageWrapper className="flex justify-center items-center flex-col mb-6 mobileCtrlUnderconstructionMessageWrapper">
        <StyledIcon>
          <Underconstruction />
        </StyledIcon>
        <h2 className="text-xl font-bold mt-3 text-center">{t('Currently under construction for mobile')}</h2>
        <p className="mt-3 text-center">
          {t(
            'This feature is part of our ongoing mobile rollout and is not available yet on mobile devices. Please switch to a desktop computer to continue.',
          )}
        </p>
        <p className="mt-3 mb-6 text-center">
          {t('Thank you for your patience while we improve the mobile experience.')}
        </p>
      </StyledMessageWrapper>

      <div className="mobileCtrlUnderconstructionButtonWrapper">
        <Button
          className="cursor-pointer pl-6 pr-6 w-fit"
          variant="text"
          onClick={() => (isChair ? history.push(getRouteByName('RouteConferences').path) : history.goBack())}
        >
          <span style={{ color: theme.palette.primary.main }}>{t('Back')}</span>
        </Button>
      </div>
    </div>
  );

  const impersonatedBar = (
    <StyledImpersonatedBar>
      <Trans i18nKey="userImpersonatedBanner">
        You are impersonated as <strong>{{ userName }}</strong>. To leave impersonation,{' '}
        <Link onClick={(e) => leaveImpersonationAction?.()} className="cursor-pointer ml-1 underline">
          click here
        </Link>
        .
      </Trans>
    </StyledImpersonatedBar>
  );

  const appMenu = (
    <StyledMenuBarWrapper
      className={`flex flex-col menuWrapper ${menuMobileOpen ? 'block' : 'hidden'}`}
      style={{ backgroundColor: theme.palette.secondary.main }}
    >
      {/* CLOSE BUTTON FOR MOBILE MENU */}
      {isMobile && (
        <div className="mobileCtrlMenuCloseButtonWrapper">
          <button className="mobileCtrlMenuCloseButton" onClick={handleOnMenuToggle}>
            <Close color={theme.palette.primary.main} width="40" />
          </button>
          <hr />
        </div>
      )}

      {/* LOGO WRAPPER */}
      {!isMobile && (
        <div className="h-16 flex items-center border-b border-gray-200">
          <div className="relative logoWrapper">{logo}</div>
        </div>
      )}

      {/* TRACK NAME FOR MOBILE MENU */}
      {isMobile && currentConference?.name && (
        <div className="mobileCtrlConferenceTrackName">
          <p>
            <span className="ml-4 font-bold">{currentConference?.name}</span>
            <span className="mobileCtrlTrack">{currentTrack?.name}</span>
          </p>
        </div>
      )}

      {/* LATERAL MENU: INSIDE OF TRACK */}
      <div className="flex-grow overflow-y-auto relative flex flex-col justify-between">
        <div>
          <Menu open={!menuOpen} className="pr-1">
            <div key="hide-menu" className="text-gray-400">
              {/*<MenuItem
                    icon={<MenuLeftIcon />}
                    label={getMenuItemName('Hide menu', t)}
                    onClick={() => {
                      doClick(
                        {
                          name: 'Hide menu',
                          route_name: 'RouteConferenceHome',
                          icon: MenuLeftIcon,
                        },
                        false,
                      );
                    }}
                  />*/}
            </div>
            {menuItems}
            {/* TODO Remove */}
            {process.env.REACT_APP_ENV != 'production' && (
              <StyledDocumentationLink href="http://docs.chairingtool.com:3000/docs/Introduction" target="_blank">
                <div className="icon">
                  <div className="w-5 pl-4">
                    <BookOpen />
                  </div>
                </div>
                <div className="label pl-7">{t('Documentation')}</div>
              </StyledDocumentationLink>
            )}
          </Menu>
        </div>

        {/* REQUEST CONFERENCE BUTTON */}
        {!isImpersonated && (
          <StyledRequestConference
            className="flex items-center pt-4 pb-4"
            style={{ color: theme.palette.primary.main }}
          >
            <AddButton
              label={t('Request Conference')}
              menuOpen={menuOpen}
              onClick={() => history.push(getRouteByName('RouteConferencesCreate').path)}
            />
          </StyledRequestConference>
        )}
      </div>

      <StyledFooter className="supportFooter border-t border-gray-50">
        <p>
          {t('Please feel free to contact us at')}
          <a href="mailto:support@chairingtool.com" className="ml-1">
            support@chairingtool.com
          </a>
        </p>
      </StyledFooter>
    </StyledMenuBarWrapper>
  );

  return (
    <StyledMainContent className="flex h-full flex-col">
      {/* IMPERSONATED BAR */}
      {isImpersonated && impersonatedBar}

      <div className={`flex ${isImpersonated ? 'mainContentImpersonation' : 'h-full'}`}>
        {/* APP MENU */}
        {menuItems.length > 0 && appMenu}

        {/* MAIN CONTENT */}
        <div
          className="flex-grow flex flex-col overflow-x-auto relative mainContent"
          style={{ backgroundColor: theme.palette.background.default, color: theme.palette.text.primary }}
        >
          {/* HEADER */}
          <div className="flex-shrink-0 flex justify-between items-center h-16 px-8 border-b border-gray-200 mobileCtrlChairingtoolHeaderWrapper">
            {header}
          </div>

          {/* CONTENT */}
          <PageLayout
            className="flex-grow overflow-auto h-full flex flex-col mobileCtrlPageLayout"
            style={{ backgroundColor }}
          >
            {isMobile && allowMobile !== true ? mobileUnderconstruction : children}
          </PageLayout>
        </div>
      </div>
    </StyledMainContent>
  );
};

const StyledDocumentationLink = styled(Link)`
  display: flex;
  align-items: center;
  color: #000 !important;
  padding-top: 0.625rem;
  padding-bottom: 0.625rem;
`;

const StyledMenuBarWrapper = styled.div`
  background: rgb(244, 245, 250);
  background: linear-gradient(0deg, rgb(223, 232, 249) 0%, rgb(228, 235, 251) 13%, rgb(250, 250, 250) 55%);
  max-width: 14.5rem;
`;

const StyledFooter = styled.div`
  background-color: rgb(223, 232, 249);
  width: 13rem;
  font-size: 0.775rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-top: 1rem;
  padding-bottom: 1rem;
  padding-left: 1rem;

  p {
    line-height: 1.4;

    a {
      color: rgb(0, 68, 240);
    }
  }
`;

const StyledImpersonatedBar = styled.div`
  z-index: 100;
  position: relative;
  font-size: 0.75rem;
  line-height: 1rem;
  text-align: center;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
  --tw-bg-opacity: 1;
  background-color: rgba(252, 211, 77, var(--tw-bg-opacity));

  a {
    color: #000 !important;
  }
`;

const StyledMainContent = styled.div`
  .mainContentImpersonation {
    height: calc(100% - 24px);
  }
`;

const PageLayout = styled.div`
  padding: 1.5rem 1.5rem 0 1.5rem;
  background-color: #edf3ff;
`;

const StyledRequestConference = styled.div`
  width: 100%;
  font-size: 0.875rem;
  display: flex;
  flex-direction: row;

  .addButton {
    margin-left: 1rem;
  }
`;

const StyledMessageWrapper = styled.div`
  max-width: 30rem;

  h2 {
    font-size: 1.45rem;
  }
  p {
    font-size: 1.1rem;
    line-height: 1.5rem;
  }
`;

const StyledIcon = styled.div`
  svg {
    width: 7.5rem;
    height: 7.5rem;
  }
`;

const StyledMenuItem = styled(MenuItem)`
  opacity: 0.2;
  cursor: pointer;
  pointer-events: none;
`;

export default MainLayout;
